import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Paper, TextField, IconButton, Typography, Stack, Chip,
  List, ListItem, ListItemText, ListItemIcon, Divider, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, CircularProgress, Fade, Collapse,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  AutoAwesome as MagicIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// Flow generation prompts and examples
const FLOW_EXAMPLES = [
  {
    title: 'Simple Chat Bot',
    description: 'Create a basic chat bot that responds to user messages',
    prompt: 'Create a simple chat bot that greets users and answers questions',
  },
  {
    title: 'Email Workflow',
    description: 'Send automated emails based on form submissions',
    prompt: 'Create a workflow that sends a welcome email when someone fills out a contact form',
  },
  {
    title: 'Data Processing',
    description: 'Process and validate data from API calls',
    prompt: 'Create a flow that fetches data from an API, validates it, and stores the results',
  },
  {
    title: 'HR Onboarding',
    description: 'Complete employee onboarding process',
    prompt: 'Create an HR onboarding flow that collects employee details, sends IT requests, and schedules orientation',
  },
];

// AI Flow Builder Component
function FlowChatBuilder({
  onGenerateFlow,
  isOpen,
  onClose,
  existingNodes = [],
  existingEdges = [],
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  const [generatedFlow, setGeneratedFlow] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 1,
        type: 'ai',
        content: "Hi! I'm your AI Flow Builder assistant. I can help you create agentic flows using natural language. Just describe what you want your flow to do, and I'll generate it for you!",
        timestamp: new Date(),
      }]);
    }
  }, [messages.length]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isGenerating) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsGenerating(true);

    try {
      // Simulate AI processing (in real implementation, this would call an AI service)
      const aiResponse = await generateFlowFromDescription(inputValue.trim());

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse.message,
        timestamp: new Date(),
        flow: aiResponse.flow,
        suggestions: aiResponse.suggestions,
      };

      setMessages((prev) => [...prev, aiMessage]);

      if (aiResponse.flow) {
        setGeneratedFlow(aiResponse.flow);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error while generating your flow. Please try again with a different description.",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateFlowFromDescription = async (description) => {
    // Simulate AI processing delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Simple pattern matching for demo (in real implementation, this would use AI/LLM)
    const lowerDesc = description.toLowerCase();

    if (lowerDesc.includes('chat') || lowerDesc.includes('conversation')) {
      return {
        message: "I've generated a simple chat bot flow for you! This flow includes a start node, an AI agent for conversation, and an end node. You can customize the AI agent's system prompt to match your specific use case.",
        flow: generateSimpleChatFlow(),
        suggestions: [
          'Add form collection to gather user information',
          'Include email notifications for new conversations',
          'Add conditional logic for different conversation paths',
        ],
      };
    }

    if (lowerDesc.includes('email') || lowerDesc.includes('welcome')) {
      return {
        message: "I've created an email workflow for you! This flow collects form data and sends automated welcome emails. You can customize the email template and form fields as needed.",
        flow: generateEmailWorkflow(),
        suggestions: [
          'Add email templates for different user types',
          'Include calendar scheduling for follow-up meetings',
          'Add database storage for form submissions',
        ],
      };
    }

    if (lowerDesc.includes('hr') || lowerDesc.includes('onboarding') || lowerDesc.includes('employee')) {
      return {
        message: "I've generated a comprehensive HR onboarding flow! This includes employee data collection, IT setup requests, calendar scheduling, and welcome communications. Perfect for streamlining your onboarding process.",
        flow: generateHROnboardingFlow(),
        suggestions: [
          'Add compliance validation checks',
          'Include manager approval workflows',
          'Add integration with your HR system',
        ],
      };
    }

    if (lowerDesc.includes('api') || lowerDesc.includes('data') || lowerDesc.includes('process')) {
      return {
        message: "I've created a data processing workflow for you! This flow fetches data from an API, processes it, and can store or forward the results. You can customize the API endpoints and processing logic.",
        flow: generateDataProcessingFlow(),
        suggestions: [
          'Add data validation and error handling',
          'Include database storage for processed data',
          'Add email notifications for processing results',
        ],
      };
    }

    // Default response for unrecognized patterns
    return {
      message: "I understand you want to create a flow, but I need a bit more detail. Could you describe what specific actions or steps you want your flow to perform? For example, 'Create a flow that sends emails when forms are submitted' or 'Build a chat bot for customer support'.",
      flow: null,
      suggestions: [
        'Try describing the specific actions you want',
        'Mention the types of integrations you need',
        'Include details about user interactions',
      ],
    };
  };

  const generateSimpleChatFlow = () => ({
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Chat Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Chat Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a helpful assistant. Respond to user messages in a friendly and informative way.',
            model: 'gpt-4',
            temperature: 0.7,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 500, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'ai-agent_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_2',
        source: 'ai-agent_1',
        target: 'end_1',
        type: 'edge-basic',
      },
    ],
    globalState: {},
  });

  const generateEmailWorkflow = () => ({
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'form_1',
        type: 'form',
        label: 'Contact Form',
        position: { x: 300, y: 100 },
        data: {
          label: 'Contact Form',
          type: 'form',
          config: {
            fields: [
              {
                name: 'name', label: 'Name', type: 'text', required: true,
              },
              {
                name: 'email', label: 'Email', type: 'email', required: true,
              },
              {
                name: 'message', label: 'Message', type: 'textarea', required: true,
              },
            ],
          },
        },
      },
      {
        id: 'email_1',
        type: 'email',
        label: 'Send Welcome Email',
        position: { x: 500, y: 100 },
        data: {
          label: 'Send Welcome Email',
          type: 'email',
          config: {
            to: '{{email}}',
            subject: 'Thank you for contacting us, {{name}}!',
            body: 'Hi {{name}},\n\nThank you for reaching out! We have received your message and will get back to you soon.\n\nBest regards,\nOur Team',
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'form_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_2',
        source: 'form_1',
        target: 'email_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_3',
        source: 'email_1',
        target: 'end_1',
        type: 'edge-basic',
      },
    ],
    globalState: {},
  });

  const generateHROnboardingFlow = () => ({
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start Onboarding',
        position: { x: 100, y: 100 },
        data: { label: 'Start Onboarding', type: 'start' },
      },
      {
        id: 'form_1',
        type: 'form',
        label: 'Employee Details',
        position: { x: 300, y: 100 },
        data: {
          label: 'Employee Details',
          type: 'form',
          config: {
            fields: [
              {
                name: 'name', label: 'Full Name', type: 'text', required: true,
              },
              {
                name: 'role', label: 'Job Role', type: 'text', required: true,
              },
              {
                name: 'startDate', label: 'Start Date', type: 'date', required: true,
              },
              {
                name: 'email', label: 'Email', type: 'email', required: true,
              },
            ],
          },
        },
      },
      {
        id: 'email_1',
        type: 'email',
        label: 'Send IT Request',
        position: { x: 500, y: 100 },
        data: {
          label: 'Send IT Request',
          type: 'email',
          config: {
            to: 'it@company.com',
            subject: 'New Employee Setup - {{name}}',
            body: 'New employee {{name}} ({{role}}) starting {{startDate}} needs IT setup.\nEmail: {{email}}',
          },
        },
      },
      {
        id: 'email_2',
        type: 'email',
        label: 'Welcome Email',
        position: { x: 700, y: 100 },
        data: {
          label: 'Welcome Email',
          type: 'email',
          config: {
            to: '{{email}}',
            subject: 'Welcome to the Team, {{name}}!',
            body: 'Hi {{name}},\n\nWelcome to our team! We\'re excited to have you join us as {{role}} starting {{startDate}}.\n\nBest regards,\nHR Team',
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'Onboarding Complete',
        position: { x: 900, y: 100 },
        data: { label: 'Onboarding Complete', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'form_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_2',
        source: 'form_1',
        target: 'email_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_3',
        source: 'email_1',
        target: 'email_2',
        type: 'edge-basic',
      },
      {
        id: 'edge_4',
        source: 'email_2',
        target: 'end_1',
        type: 'edge-basic',
      },
    ],
    globalState: {},
  });

  const generateDataProcessingFlow = () => ({
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'api-call_1',
        type: 'api-call',
        label: 'Fetch Data',
        position: { x: 300, y: 100 },
        data: {
          label: 'Fetch Data',
          type: 'api-call',
          config: {
            url: 'https://api.example.com/data',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        },
      },
      {
        id: 'code-executor_1',
        type: 'code-executor',
        label: 'Process Data',
        position: { x: 500, y: 100 },
        data: {
          label: 'Process Data',
          type: 'code-executor',
          config: {
            code: '// Process the API response data\nconst processedData = data.map(item => ({\n  id: item.id,\n  name: item.name,\n  processed: true\n}));\n\nreturn { processedData };',
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'api-call_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_2',
        source: 'api-call_1',
        target: 'code-executor_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_3',
        source: 'code-executor_1',
        target: 'end_1',
        type: 'edge-basic',
      },
    ],
    globalState: {},
  });

  const handleApplyFlow = () => {
    if (generatedFlow) {
      onGenerateFlow(generatedFlow);
      onClose();
    }
  };

  const handleExampleClick = (example) => {
    setInputValue(example.prompt);
  };

  const clearChat = () => {
    setMessages([{
      id: 1,
      type: 'ai',
      content: "Hi! I'm your AI Flow Builder assistant. I can help you create agentic flows using natural language. Just describe what you want your flow to do, and I'll generate it for you!",
      timestamp: new Date(),
    }]);
    setGeneratedFlow(null);
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <MagicIcon color="primary" />
            <Typography variant="h6">AI Flow Builder</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Clear Chat">
              <IconButton size="small" onClick={clearChat}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Examples Section */}
        <Collapse in={showExamples}>
          <Box sx={{
            p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider',
          }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Try these examples:
              </Typography>
              <IconButton size="small" onClick={() => setShowExamples(!showExamples)}>
                {showExamples ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {FLOW_EXAMPLES.map((example, index) => (
                <Chip
                  key={index}
                  label={example.title}
                  size="small"
                  variant="outlined"
                  onClick={() => handleExampleClick(example)}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        </Collapse>

        {/* Chat Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <List sx={{ p: 0 }}>
            {messages.map((message) => (
              <ListItem key={message.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  {message.type === 'user' ? (
                    <PersonIcon color="primary" fontSize="small" />
                  ) : (
                    <AIIcon color="secondary" fontSize="small" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Stack>

                <Paper
                  sx={{
                    p: 2,
                    bgcolor: message.type === 'user' ? 'primary.light' : 'grey.100',
                    color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
                    maxWidth: '80%',
                    ml: message.type === 'user' ? 'auto' : 0,
                    mr: message.type === 'user' ? 0 : 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>

                  {message.isError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <ErrorIcon fontSize="small" />
                      Error generating flow
                    </Alert>
                  )}

                  {message.flow && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="success" sx={{ mb: 1 }}>
                        <CheckIcon fontSize="small" />
                        Flow generated successfully!
                      </Alert>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setShowPreview(true)}
                        startIcon={<MagicIcon />}
                      >
                        Preview Flow
                      </Button>
                    </Box>
                  )}

                  {message.suggestions && message.suggestions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Suggestions for enhancement:
                      </Typography>
                      {message.suggestions.map((suggestion, index) => (
                        <Chip
                          key={index}
                          label={suggestion}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              </ListItem>
            ))}

            {isGenerating && (
              <ListItem sx={{ justifyContent: 'center' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Generating your flow...
                  </Typography>
                </Stack>
              </ListItem>
            )}

            <div ref={messagesEndRef} />
          </List>
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              placeholder="Describe the flow you want to create... (e.g., 'Create a chat bot for customer support')"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              disabled={isGenerating}
              multiline
              maxRows={3}
              size="small"
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isGenerating}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogContent>

      {/* Flow Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MagicIcon color="primary" />
            <Typography variant="h6">Generated Flow Preview</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {generatedFlow && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This flow contains
                {' '}
                {generatedFlow.nodes.length}
                {' '}
                nodes and
                {' '}
                {generatedFlow.edges.length}
                {' '}
                connections.
              </Typography>
              <Box sx={{
                bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2,
              }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Nodes:</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {generatedFlow.nodes.map((node) => (
                    <Chip
                      key={node.id}
                      label={node.label}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyFlow}
            startIcon={<CheckIcon />}
          >
            Apply to Canvas
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default FlowChatBuilder;
